import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Users, 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Shield, 
  ShieldOff, 
  Eye, 
  EyeOff,
  X,
  AlertCircle,
  CheckCircle,
  Filter,
  MoreVertical
} from 'lucide-react';
import RoleManagementModal from '../components/RoleManagementModal';
import RoleEditorModal from '../components/RoleEditorModal';

const UserManagement = () => {
  // Auth and permissions
  const { user, token, canAccessManager, loading: authLoading } = useAuth();
  
  // State management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [pageSize] = useState(10);
  
  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Role Management Modals
  const [showRoleManagement, setShowRoleManagement] = useState(false);
  const [showRoleEditor, setShowRoleEditor] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  
  // Custom Roles from Manage Roles
  const [customRoles, setCustomRoles] = useState([]);
  
  // Forms
  const [createForm, setCreateForm] = useState({
    fullName: '',
    email: '',
    password: '',
    role: 'user',
    isEmployeeAlso: false  // New field for owners who are also employees
  });
  const [editForm, setEditForm] = useState({
    fullName: '',
    password: '',
    role: '',
    isEmployeeAlso: false,  // For owner role
    isActive: true,
    isServiceProvider: false
  });
  
  // Messages
  const [message, setMessage] = useState({ text: '', type: '' });
  
  // Registration settings
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [registrationLoading, setRegistrationLoading] = useState(false);
  
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  // Fetch custom roles from Manage Roles
  const fetchCustomRoles = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/roles`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCustomRoles(data.roles || []);
      }
    } catch (err) {
      console.error('Error fetching custom roles:', err);
    }
  };

  // Role options - use custom roles from Manage Roles
  const getRoleOptions = () => {
    // Return custom roles if available
    if (customRoles.length > 0) {
      return customRoles.map(role => ({
        value: role.id,
        label: role.name,
        id: role.id
      }));
    }
    
    // Fallback to base roles if custom roles not loaded
    if (user?.role === 'admin') {
      return [
        { value: 'user', label: 'User' },
        { value: 'technician', label: 'Technician' },
        { value: 'manager', label: 'Manager' },
        { value: 'owner', label: 'Owner' },
        { value: 'admin', label: 'Admin' }
      ];
    } else if (user?.role === 'owner') {
      return [
        { value: 'user', label: 'User' },
        { value: 'technician', label: 'Technician' },
        { value: 'manager', label: 'Manager' },
        { value: 'owner', label: 'Owner' }
      ];
    } else if (user?.role === 'manager') {
      return [
        { value: 'user', label: 'User' },
        { value: 'technician', label: 'Technician' }
      ];
    }
    return [];
  };

  // Fetch users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: pageSize.toString(),
        search: searchTerm,
        role_filter: roleFilter
      });

      const response = await fetch(`${API_BASE_URL}/api/users?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        let filteredUsers = data.users;
        
        // Filter out admin users if current user is owner
        if (user?.role === 'owner') {
          filteredUsers = data.users.filter(u => u.role !== 'admin');
        }
        
        setUsers(filteredUsers);
        setTotalUsers(filteredUsers.length); // Update total count for filtered results
      } else {
        setError('Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch registration settings
  const fetchRegistrationSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cms-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRegistrationEnabled(data.registration_enabled !== false);
      }
    } catch (error) {
      console.error('Error fetching registration settings:', error);
    }
  };

  // Toggle registration setting
  const handleToggleRegistration = async () => {
    if (user?.role !== 'admin') {
      setMessage({ text: 'Only admins can change registration settings', type: 'error' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      return;
    }

    setRegistrationLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/cms-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          registration_enabled: !registrationEnabled
        }),
      });

      if (response.ok) {
        setRegistrationEnabled(!registrationEnabled);
        setMessage({ 
          text: `Public registration ${!registrationEnabled ? 'enabled' : 'disabled'} successfully!`, 
          type: 'success' 
        });
        setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      } else {
        setMessage({ text: 'Failed to update registration settings', type: 'error' });
        setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      }
    } catch (error) {
      console.error('Error updating registration settings:', error);
      setMessage({ text: 'Network error. Please try again.', type: 'error' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } finally {
      setRegistrationLoading(false);
    }
  };

  // Create user
  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    try {
      // Check if selected role is a custom role ID or base role
      const selectedRole = customRoles.find(r => r.id === createForm.role);
      
      // Map camelCase to snake_case for backend
      const userData = {
        email: createForm.email,
        password: createForm.password,
        full_name: createForm.fullName,
        role: selectedRole ? selectedRole.name.toLowerCase() : createForm.role, // Use custom role name as base role
        custom_role_id: selectedRole ? selectedRole.id : null,
        is_employee_also: createForm.role === 'owner' ? createForm.isEmployeeAlso : false
      };

      const response = await fetch(`${API_BASE_URL}/api/register`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (response.ok) {
        setMessage({ text: 'User created successfully!', type: 'success' });
        setShowCreateModal(false);
        setCreateForm({ fullName: '', email: '', password: '', role: 'user', isEmployeeAlso: false });
        fetchUsers();
        
        setTimeout(() => setMessage({ text: '', type: '' }), 5000);
      } else {
        const errorData = await response.json();
        let errorMessage = 'Failed to create user';
        
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // Handle FastAPI validation errors
            errorMessage = errorData.detail.map(err => err.msg || err).join(', ');
          } else if (typeof errorData.detail === 'object' && errorData.detail.msg) {
            errorMessage = errorData.detail.msg;
          }
        }
        
        setMessage({ text: errorMessage, type: 'error' });
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setMessage({ text: 'Network error. Please try again.', type: 'error' });
    }
  };

  // Update user
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    try {
      const updateData = {};
      if (editForm.fullName) updateData.full_name = editForm.fullName;
      if (editForm.password) updateData.password = editForm.password;
      
      // Handle custom role assignment
      if (editForm.role && (user?.role === 'admin' || user?.role === 'owner')) {
        const selectedRole = customRoles.find(r => r.id === editForm.role);
        console.log('🔍 Update role logic:', {
          editFormRole: editForm.role,
          selectedRole: selectedRole,
          customRoles: customRoles
        });
        
        if (selectedRole) {
          // Assign custom role - base role maps to custom role name (lowercase)
          const baseRoleName = selectedRole.name.toLowerCase();
          updateData.role = baseRoleName; // Set base role to match custom role name
          updateData.custom_role_id = selectedRole.id;
        } else {
          // Base role selected (shouldn't happen since all users use custom roles now)
          updateData.role = editForm.role;
          updateData.custom_role_id = null;
        }
      }
      
      console.log('📤 Sending update data:', updateData);
      
      // Add is_employee_also field for owner role updates
      // For owners: if "Employee is active" is checked, they should show in employee list
      if (editForm.role === 'owner' || selectedUser.role === 'owner') {
        updateData.is_employee_also = editForm.isActive;  // Set based on isActive checkbox
      }

      const response = await fetch(`${API_BASE_URL}/api/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        // Also update employee config if isActive or isServiceProvider changed
        try {
          const configData = {
            is_active: editForm.isActive,
            is_service_provider: editForm.isServiceProvider
          };
          
          console.log('📤 Updating employee config:', configData);
          
          const configResponse = await fetch(`${API_BASE_URL}/api/employee-configs/${selectedUser.id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(configData),
          });
          
          if (configResponse.ok) {
            console.log('✅ Employee config updated successfully');
          } else {
            const errorData = await configResponse.json();
            console.error('❌ Error updating employee config:', errorData);
          }
        } catch (err) {
          console.error('❌ Exception updating employee config:', err);
        }
        
        setMessage({ text: 'User updated successfully!', type: 'success' });
        setShowEditModal(false);
        setSelectedUser(null);
        setEditForm({ fullName: '', password: '', role: '', isEmployeeAlso: false, isActive: true, isServiceProvider: false });
        fetchUsers();
        
        setTimeout(() => setMessage({ text: '', type: '' }), 5000);
      } else {
        const errorData = await response.json();
        let errorMessage = 'Failed to update user';
        
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // Handle FastAPI validation errors
            errorMessage = errorData.detail.map(err => err.msg || err).join(', ');
          } else if (typeof errorData.detail === 'object' && errorData.detail.msg) {
            errorMessage = errorData.detail.msg;
          }
        }
        
        setMessage({ text: errorMessage, type: 'error' });
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setMessage({ text: 'Network error. Please try again.', type: 'error' });
    }
  };

  // Block/Unblock user
  const handleBlockUser = async (userId, isBlocked) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${userId}/block`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          isBlocked: !isBlocked,
          reason: !isBlocked ? 'Blocked by admin/manager' : undefined
        }),
      });

      if (response.ok) {
        const action = !isBlocked ? 'blocked' : 'unblocked';
        setMessage({ text: `User ${action} successfully!`, type: 'success' });
        fetchUsers();
        
        setTimeout(() => setMessage({ text: '', type: '' }), 5000);
      } else {
        const errorData = await response.json();
        setMessage({ text: errorData.detail || 'Failed to update user status', type: 'error' });
      }
    } catch (error) {
      console.error('Error blocking/unblocking user:', error);
      setMessage({ text: 'Network error. Please try again.', type: 'error' });
    }
  };

  // Delete user
  const handleDeleteUser = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${selectedUser.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setMessage({ text: 'User deleted successfully!', type: 'success' });
        setShowDeleteModal(false);
        setSelectedUser(null);
        fetchUsers();
        
        setTimeout(() => setMessage({ text: '', type: '' }), 5000);
      } else {
        const errorData = await response.json();
        setMessage({ text: errorData.detail || 'Failed to delete user', type: 'error' });
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setMessage({ text: 'Network error. Please try again.', type: 'error' });
    }
  };

  // Open edit modal
  const openEditModal = (user) => {
    console.log('🔍 Opening edit modal for user:', {
      fullName: user.fullName,
      role: user.role,
      customRoleId: user.customRoleId,
      roleName: user.roleName
    });
    
    // Determine the correct role value for dropdown
    let roleValue = user.customRoleId;
    
    // If user doesn't have custom_role_id, try to find matching custom role by name
    if (!roleValue && customRoles.length > 0) {
      const matchingRole = customRoles.find(r => 
        r.name.toLowerCase() === (user.roleName || user.role || '').toLowerCase()
      );
      if (matchingRole) {
        roleValue = matchingRole.id;
        console.log('🔍 Found matching custom role by name:', matchingRole.name, matchingRole.id);
      } else {
        console.log('⚠️ No matching custom role found for:', user.roleName || user.role);
        // Default to first role if no match
        roleValue = customRoles[0]?.id || '';
      }
    }
    
    console.log('🔍 Setting editForm.role to:', roleValue);
    
    setSelectedUser(user);
    setEditForm({
      fullName: user.fullName,
      password: '',
      role: roleValue || '',
      isEmployeeAlso: user.is_employee_also || false,
      isActive: user.is_active !== undefined ? user.is_active : true,
      isServiceProvider: user.is_service_provider || false
    });
    setShowEditModal(true);
  };

  // Open delete modal
  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  // Handle search and filter changes
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      setCurrentPage(1);
      fetchUsers();
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, roleFilter]);

  // Fetch users on mount and page change
  useEffect(() => {
    if (!authLoading && canAccessManager()) {
      fetchUsers();
      fetchCustomRoles(); // Fetch custom roles for role dropdown
      // Fetch registration settings for admins
      if (user?.role === 'admin') {
        fetchRegistrationSettings();
      }
      // Scroll to top when page changes
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [authLoading, canAccessManager, currentPage]);

  // Show loading while authentication is being determined
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Check permissions
  if (!canAccessManager()) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h2>
            <p className="text-red-600">You need manager or admin privileges to access user management.</p>
          </div>
        </div>
      </div>
    );
  }

  const totalPages = Math.ceil(totalUsers / pageSize);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header - Compact Design like In Review Page */}
          <div className="bg-white border-b border-gray-200">
            <div className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-3">
                    User Management
                    <span className="text-xs font-normal text-gray-500">
                      Total: {totalUsers}
                    </span>
                  </h2>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  {/* Manage Roles Button - Admin Only */}
                  {user?.role === 'admin' && (
                    <button
                      onClick={() => setShowRoleManagement(true)}
                      className="text-white text-sm px-3 py-1.5 rounded-md transition-colors font-medium flex items-center justify-center"
                      style={{ backgroundColor: '#3b82f6' }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#2563eb';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#3b82f6';
                      }}
                    >
                      <Shield className="h-4 w-4 mr-1.5" />
                      Manage Roles
                    </button>
                  )}
                  {/* Add User Button */}
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="text-white text-sm px-3 py-1.5 rounded-md transition-colors font-medium flex items-center justify-center"
                    style={{ backgroundColor: '#10b981' }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#059669';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#10b981';
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1.5" />
                    Add User
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">

            {/* Success/Error Messages */}
            {message.text && (
              <div className={`mb-6 p-4 rounded-lg border ${
                message.type === 'success' 
                  ? 'bg-green-50 border-green-200 text-green-800' 
                  : 'bg-red-50 border-red-200 text-red-800'
              }`}>
                <div className="flex items-center">
                  {message.type === 'success' ? (
                    <CheckCircle className="h-5 w-5 mr-2" />
                  ) : (
                    <AlertCircle className="h-5 w-5 mr-2" />
                  )}
                  {message.text}
                </div>
              </div>
            )}

            {/* Registration Settings - Admin Only */}
            {user?.role === 'admin' && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Shield className="h-5 w-5 text-theme-blue" />
                    <div>
                      <h3 className="font-medium text-gray-900">Public Registration</h3>
                      <p className="text-sm text-gray-600">
                        {registrationEnabled 
                          ? 'New users can register on their own' 
                          : 'Only admins can create new users from this page'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleToggleRegistration}
                    disabled={registrationLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      registrationEnabled ? 'bg-theme-blue' : 'bg-gray-300'
                    } ${registrationLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        registrationEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            )}

            {/* Search and Filters */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search users by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                  />
                </div>
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                  >
                    <option value="">All Roles</option>
                    <option value="user">Users</option>
                    <option value="technician">Technicians</option>
                    {(user?.role === 'admin' || user?.role === 'owner') && <option value="manager">Managers</option>}
                    {(user?.role === 'admin' || user?.role === 'owner') && <option value="owner">Owners</option>}
                    {user?.role === 'admin' && <option value="admin">Admins</option>}
                  </select>
                </div>
              </div>
            </div>

            {/* Users Table */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue mr-3"></div>
                            Loading users...
                          </div>
                        </td>
                      </tr>
                    ) : users.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                          No users found.
                        </td>
                      </tr>
                    ) : (
                      users.map((userItem) => (
                        <tr key={userItem.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{userItem.fullName}</div>
                              <div className="text-sm text-gray-500">{userItem.email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-3 py-1 rounded text-xs font-medium ${
                              userItem.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                              userItem.role === 'owner' ? 'bg-orange-100 text-orange-800' :
                              userItem.role === 'manager' ? 'bg-blue-100 text-blue-800' :
                              userItem.role === 'technician' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {userItem.roleName || (userItem.role.charAt(0).toUpperCase() + userItem.role.slice(1))}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-3 py-1 rounded text-xs font-medium ${
                              userItem.isBlocked ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {userItem.isBlocked ? 'Blocked' : 'Active'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {userItem.createdAt ? new Date(userItem.createdAt).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => openEditModal(userItem)}
                                className="text-theme-blue hover:text-blue-900 p-1 rounded"
                                title="Edit user"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleBlockUser(userItem.id, userItem.isBlocked)}
                                className={`p-1 rounded ${
                                  userItem.isBlocked ? 'text-green-600 hover:text-green-900' : 'text-orange-600 hover:text-orange-900'
                                }`}
                                title={userItem.isBlocked ? 'Unblock user' : 'Block user'}
                              >
                                {userItem.isBlocked ? <Shield className="h-4 w-4" /> : <ShieldOff className="h-4 w-4" />}
                              </button>
                              <button
                                onClick={() => openDeleteModal(userItem)}
                                className="text-red-600 hover:text-red-900 p-1 rounded"
                                title="Delete user"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalUsers)} of {totalUsers} users
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1 text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-screen overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Create New User</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <form onSubmit={handleCreateUser} className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  required
                  value={createForm.fullName}
                  onChange={(e) => setCreateForm(prev => ({...prev, fullName: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={createForm.email}
                  onChange={(e) => setCreateForm(prev => ({...prev, email: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  required
                  value={createForm.password}
                  onChange={(e) => setCreateForm(prev => ({...prev, password: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  value={createForm.role}
                  onChange={(e) => setCreateForm(prev => ({...prev, role: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                >
                  {getRoleOptions().map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Show "Is Employee Also" checkbox only for Owner role */}
              {createForm.role === 'owner' && (
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="create_is_employee_also"
                    checked={createForm.isEmployeeAlso}
                    onChange={(e) => setCreateForm(prev => ({...prev, isEmployeeAlso: e.target.checked}))}
                    className="w-4 h-4 text-theme-blue bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="create_is_employee_also" className="text-sm font-medium text-gray-700">
                    Is owner an employee also?
                    <span className="text-gray-500 text-xs block">
                      (Enable to give owner access to employee features like timesheets, paystubs, etc.)
                    </span>
                  </label>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: 'var(--theme-primary)' }}
                >
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
            {/* Sticky Header */}
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between sticky top-0 z-10" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <h3 className="text-base font-semibold text-white">Edit User</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-white hover:text-gray-100"
                type="button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Scrollable Content */}
            <form onSubmit={handleUpdateUser} className="flex flex-col flex-1 overflow-hidden">
              <div className="px-4 py-4 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  value={editForm.fullName}
                  onChange={(e) => setEditForm(prev => ({...prev, fullName: e.target.value}))}
                  placeholder="Enter full name"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-theme-blue placeholder:text-sm placeholder:text-gray-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password (leave blank to keep current)
                </label>
                <input
                  type="password"
                  value={editForm.password}
                  onChange={(e) => setEditForm(prev => ({...prev, password: e.target.value}))}
                  placeholder="Enter new password"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-theme-blue placeholder:text-sm placeholder:text-gray-400"
                />
              </div>
              {(user?.role === 'admin' || user?.role === 'owner') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    value={editForm.role || ''}
                    onChange={(e) => {
                      console.log('Role selection changed to:', e.target.value);
                      setEditForm(prev => ({...prev, role: e.target.value}));
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                  >
                    {getRoleOptions().map(role => {
                      console.log('Role option:', role.label, 'value:', role.value, 'selected:', editForm.role);
                      return (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}

              {/* Employee is Active */}
              <div className="flex items-center space-x-2 mb-3">
                <input
                  type="checkbox"
                  id="edit_is_active"
                  checked={editForm.isActive}
                  onChange={(e) => setEditForm(prev => ({...prev, isActive: e.target.checked}))}
                  className="w-4 h-4 text-theme-blue bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="edit_is_active" className="text-sm font-medium text-gray-700">
                  Employee is active (can clock in/out and access system)
                </label>
              </div>

              {/* Service Provider */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="edit_is_service_provider"
                  checked={editForm.isServiceProvider}
                  onChange={(e) => setEditForm(prev => ({...prev, isServiceProvider: e.target.checked}))}
                  className="w-4 h-4 text-theme-blue bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="edit_is_service_provider" className="text-sm font-medium text-gray-700">
                  Service Provider (available for service appointments)
                </label>
              </div>

              </div>
              
              {/* Sticky Footer */}
              <div className="px-4 py-3 border-t border-gray-200 flex justify-end space-x-3 sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm text-white rounded-lg hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: 'var(--theme-primary)' }}
                >
                  Update User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Delete User</h3>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete <strong>{selectedUser.fullName}</strong>? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Role Management Modal */}
      <RoleManagementModal
        isOpen={showRoleManagement}
        onClose={() => setShowRoleManagement(false)}
        onEditRole={(role) => {
          setSelectedRole(role);
          setShowRoleEditor(true);
        }}
        onCreateRole={() => {
          setSelectedRole(null);
          setShowRoleEditor(true);
        }}
        token={token}
      />

      {/* Role Editor Modal */}
      <RoleEditorModal
        isOpen={showRoleEditor}
        onClose={() => {
          setShowRoleEditor(false);
          setSelectedRole(null);
        }}
        role={selectedRole}
        onSuccess={() => {
          setShowRoleEditor(false);
          setSelectedRole(null);
          // Refresh role list in management modal if it's open
          if (showRoleManagement) {
            setShowRoleManagement(false);
            setTimeout(() => setShowRoleManagement(true), 100);
          }
        }}
        token={token}
      />
    </div>
  );
};

export default UserManagement;